import ViewOOPModal from '@/components/sharedModals/ViewOOPModal';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from 'date-fns';
import { CreditCard, Eye, Printer, Receipt, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ViewPaymentProofModal from './PaymentProofComponents/ViewPaymentProofModal';
import ViewORModal from './ViewORModal';

const UserOOPCard = ({ oop, onRefetch }) => {
   const navigate = useNavigate();
   const [isViewORModalOpen, setIsViewORModalOpen] = useState(false);
   const [isViewOOPModalOpen, setIsViewOOPModalOpen] = useState(false);
   const [isViewPaymentProofModalOpen, setIsViewPaymentProofModalOpen] = useState(false);

   const formatDate = (timestamp) => {
      const date = new Date(parseInt(timestamp));
      return format(date, 'MMM d, yyyy');
   };

   const getStatusBadgeStyle = (status) => {
      switch (status) {
         case 'Payment Proof Submitted':
            return 'bg-yellow-50 hover:bg-yellow-50 text-yellow-700 hover:text-yellow-700 border border-yellow-200';
         case 'Payment Proof Approved':
            return 'bg-green-50 hover:bg-green-50 text-green-700 hover:text-green-700 border border-green-200';
         case 'Payment Proof Rejected':
            return 'bg-red-50 hover:bg-red-50 text-red-700 hover:text-red-700 border border-red-200';
         case 'Awaiting Payment':
            return 'bg-blue-50 hover:bg-blue-50 text-blue-700 hover:text-blue-700 border border-blue-200';
         case 'Issued OR':
            return 'bg-purple-50 hover:bg-purple-50 text-purple-700 hover:text-purple-700 border border-purple-200';
         case 'Completed OOP':
            return 'bg-green-50 hover:bg-green-50 text-green-700 hover:text-green-700 border border-green-200';
         default:
            return 'bg-gray-50 hover:bg-gray-50 text-gray-700 hover:text-gray-700 border border-gray-200';
      }
   };

   const handleViewOR = () => {
      setIsViewORModalOpen(true);
   };

   const handlePrint = () => {
      navigate('/user/oop-print', { state: { oop } });
   };

   const handlePayClick = () => {
      navigate(`/payment/${oop._id}`);
      if (onRefetch) {
         onRefetch();
      }
   };

   const showPaymentProof = ['Payment Proof Submitted', 'Payment Proof Approved', 'Payment Proof Rejected'].includes(oop.OOPstatus);

   const handleUndoPaymentProof = async () => {
      try {
         await undoPaymentProof({
            variables: {
               oopId: oop._id
            }
         });
         toast.success('Payment proof undone successfully');
         if (onRefetch) {
            await onRefetch();
         }
      } catch (error) {
         console.error('Error undoing payment proof:', error);
         toast.error('Failed to undo payment proof');
      }
   };

   // Get actions based on OOP status
   const getActions = () => {
      const actions = [
         {
            icon: Eye,
            label: "View OOP",
            onClick: () => setIsViewOOPModalOpen(true),
            variant: "outline",
            className: "view-oop-action"
         },
         {
            icon: Printer,
            label: "Print OOP",
            onClick: handlePrint,
            variant: "outline",
            className: "print-oop-action"
         }
      ];

      if (oop.OOPstatus === 'Awaiting Payment') {
         actions.push({
            icon: CreditCard,
            label: "Pay",
            onClick: handlePayClick,
            variant: "outline",
            className: "pay-action text-blue-600"
         });
      }

      if (oop.OOPstatus === 'Issued OR') {
         actions.push({
            icon: Receipt,
            label: "View Official Receipt",
            onClick: handleViewOR,
            variant: "outline",
            className: "view-or-action text-purple-600"
         });
      }

      if (showPaymentProof) {
         actions.push({
            icon: Receipt,
            label: "View Payment Proof",
            onClick: () => setIsViewPaymentProofModalOpen(true),
            variant: "outline",
            className: "view-payment-proof-action"
         });
      }

      if (oop.OOPstatus === 'Payment Proof Submitted') {
         actions.push({
            icon: RotateCcw,
            label: "Undo Payment Proof",
            onClick: handleUndoPaymentProof,
            variant: "outline",
            className: "undo-payment-proof-action text-yellow-600"
         });
      }

      return actions;
   };

   // Render action buttons with tooltips
   const renderActionButtons = () => {
      const actions = getActions();

      return (
         <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Actions:</p>
            <div className="flex flex-wrap gap-2">
               {actions.map((action, index) => (
                  <TooltipProvider key={index} delayDuration={200}>
                     <Tooltip>
                        <TooltipTrigger asChild>
                           <Button
                              variant="outline"
                              size="icon"
                              onClick={action.onClick}
                              className={`${action.className} h-9 w-9 p-0`}
                           >
                              <action.icon className="h-4 w-4" />
                           </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                           <p>{action.tooltip || action.label}</p>
                        </TooltipContent>
                     </Tooltip>
                  </TooltipProvider>
               ))}
            </div>
         </div>
      );
   };

   return (
      <>
         <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 bg-white dark:bg-gray-800 dark:border-gray-700">
            {/* Status Banner */}
            <div className={`h-2 ${getStatusBadgeStyle(oop.OOPstatus)}`} />

            <div className="p-6">
               {/* Header Section */}
               <div className="flex justify-between items-start mb-4">
                  <div>
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {oop.applicationNumber}
                     </h3>
                     <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Bill No: {oop.billNo}
                     </p>
                  </div>
                  <Badge className={`${getStatusBadgeStyle(oop.OOPstatus)} px-3 py-1 dark:border-opacity-50`}>
                     {oop.OOPstatus}
                  </Badge>
               </div>

               {/* Details Section */}
               <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Amount</p>
                        <p className="mt-1 dark:text-gray-300">₱{oop.totalAmount?.toFixed(2)}</p>
                     </div>
                     <div className="text-right">
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Date Created</p>
                        <p className="mt-1 dark:text-gray-300">
                           {formatDate(oop.createdAt)}
                        </p>
                     </div>
                  </div>
               </div>

               {/* Actions Section */}
               {renderActionButtons()}
            </div>
         </Card>

         {/* Modals */}
         <ViewORModal
            isOpen={isViewORModalOpen}
            onClose={() => setIsViewORModalOpen(false)}
            oop={oop}
         />
         <ViewOOPModal
            isOpen={isViewOOPModalOpen}
            onClose={() => setIsViewOOPModalOpen(false)}
            oop={oop}
         />
         <ViewPaymentProofModal
            isOpen={isViewPaymentProofModalOpen}
            onClose={() => setIsViewPaymentProofModalOpen(false)}
            paymentProof={oop.paymentProof}
         />
      </>
   );
};

export default UserOOPCard;